import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  CustomizationNoDressingModal,
  CustomizationOutOfStockModal,
  CustomizationRequiredModificationModal,
  CustomizationTooFewBasesModal,
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

// ────────────────────────────────────────────────────────────────────────────────

export const CustomizationModals = (props: Props) => {
  const {
    actor,
    isOutOfStockModalOpen,
    closeOutOfStockModal,
    setScrollTargetId,
  } = props;

  const { trackEvent } = useTelemetry();

  // ─────────────────────────────────────────────────────────────────────

  const {
    outOfStockIngredients,
    product,
    modifications,
    restaurant,
    shouldShowNoDressingModal,
    shouldShowTooFewBasesModal,
    shouldShowRequiredModificationModal,
    requiredIngredientName,
    activeModifications,
  } = useCustomizationContext({ actor });

  const { continueCustomization, selectDetails } = useCustomizationHelpers({
    actor,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackNoDressingModalViewEvent = useCallback(() => {
    trackEvent({
      name: 'pdp.view.modal.no-dressing',
      payload: {
        selectedProduct: telemetryProductFromInput(product, modifications),
      },
    });
  }, [trackEvent, product, modifications]);

  const trackTooFewBasesModalViewEvent = useCallback(() => {
    trackEvent({
      name: 'pdp.view.modal.too-few-bases',
      payload: {
        selectedProduct: telemetryProductFromInput(product, modifications),
      },
    });
  }, [trackEvent, product, modifications]);

  // ─────────────────────────────────────────────────────────────────────

  if (!product || !restaurant) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <CustomizationTooFewBasesModal
        isVisible={shouldShowTooFewBasesModal}
        activeModifications={activeModifications}
        onRequestClose={continueCustomization}
        onContinuePress={continueCustomization}
        onEndPress={selectDetails}
        onShow={trackTooFewBasesModalViewEvent}
      />
      <CustomizationNoDressingModal
        isVisible={shouldShowNoDressingModal}
        onGoBackPress={continueCustomization}
        onContinuePress={selectDetails}
        onRequestClose={continueCustomization}
        onShow={trackNoDressingModalViewEvent}
      />
      <CustomizationOutOfStockModal
        isVisible={isOutOfStockModalOpen}
        onRequestClose={closeOutOfStockModal}
        outOfStockIngredients={outOfStockIngredients}
        productName={product.name}
        restaurantName={restaurant.name}
        onChooseNewIngredientPress={setScrollTargetId}
      />
      <CustomizationRequiredModificationModal
        isVisible={shouldShowRequiredModificationModal}
        productName={product.name}
        onConfirm={continueCustomization}
        requiredIngredientName={requiredIngredientName}
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flex: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  isOutOfStockModalOpen: boolean;
  closeOutOfStockModal: () => void;
  setScrollTargetId?: (id: string) => void;
};
